import React from 'react';
import { useParams } from 'react-router';
import { TestConnectionResponse } from 'modules/clients/interfaces';
import { TEST_CONNECTION_STORAGE_KEY } from '../simulateApp';

type UseSimulateAppParams = {
  testConnection: TestConnectionResponse | null;
  saveUrlConnectionRequest: (props) => void;
  saveChromeExtensionRequest: (props) => void;
  clearTestOpenApplication: () => void;
};

export const useSimulateApp = ({
  testConnection,
  saveUrlConnectionRequest,
  saveChromeExtensionRequest,
  clearTestOpenApplication,
}: UseSimulateAppParams) => {
  const [pendingOpen, setPendingOpen] = React.useState<{
    path: string;
    expectedUrl: string;
  } | null>(null);
  const { id } = useParams<{ id: string }>();

  const [openInBrowserUrl, setOpenInBrowserUrl] = React.useState('');
  const [openRemote, setOpenRemote] = React.useState('');

  React.useEffect(() => {
    return () => {
      clearTestOpenApplication();
      localStorage.removeItem(TEST_CONNECTION_STORAGE_KEY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTestOpenApplication]);

  React.useEffect(() => {
    clearTestOpenApplication();
    localStorage.removeItem(TEST_CONNECTION_STORAGE_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  React.useEffect(() => {
    if (testConnection) {
      if (
        [
          'direct_rdp',
          'direct_connect',
          'rdp_remote_app',
          'tsplus',
          'tsplus_all',
          'rdp_direct',
          'rdp_app',
        ].includes(testConnection?.virtualizer)
      ) {
        setOpenInBrowserUrl(testConnection?.virtualizer);
      }

      if (
        [
          'rdp',
          'tsplus_remote_app',
          'tsplus_all',
          'connect',
          'connect_direct',
        ].includes(testConnection?.virtualizer)
      ) {
        setOpenRemote(testConnection?.virtualizer);
      }

      localStorage.setItem(
        TEST_CONNECTION_STORAGE_KEY,
        JSON.stringify(testConnection)
      );
    }
  }, [testConnection]);

  const handleOpenInBrowser = () => {
    if (openInBrowserUrl.startsWith('tsplus')) {
      window.open('/clients/applications/simulate');
    } else if (
      openInBrowserUrl.startsWith('rdp_direct') ||
      openInBrowserUrl.startsWith('rdp_app')
    ) {
      saveUrlConnectionRequest(testConnection?.url);
      setTimeout(() => {
        window.open(`/clients/applications/simulate/remoteapp`);
      }, 1000);
    } else {
      window.open(testConnection?.url);
    }
  };

  React.useEffect(() => {
    if (pendingOpen && testConnection?.url === pendingOpen.expectedUrl) {
      window.open(pendingOpen.path);
      setPendingOpen(null);
    }
  }, [testConnection, pendingOpen]);

  const handleOpenRemote = () => {
    const virtualizer = testConnection?.virtualizer ?? '';

    if (
      virtualizer.startsWith('tsplus') ||
      virtualizer === 'tsplus_remote_app'
    ) {
      const expectedUrl = testConnection?.url ?? '';
      saveUrlConnectionRequest(expectedUrl);
      setPendingOpen({
        path: '/clients/applications/simulate/remoteapp',
        expectedUrl,
      });
    } else if (virtualizer === 'rdp') {
      const expectedUrl = testConnection?.rdp_file_contents ?? '';
      saveUrlConnectionRequest(expectedUrl);
      setPendingOpen({
        path: '/clients/applications/simulate/rdp/file',
        expectedUrl,
      });
    } else if (virtualizer === 'connect') {
      const expectedUrl = testConnection?.url ?? '';
      saveUrlConnectionRequest(expectedUrl);
      saveChromeExtensionRequest(testConnection?.chrome_url);
      setPendingOpen({
        path: '/clients/applications/simulate/connectSky',
        expectedUrl,
      });
    } else if (virtualizer === 'connect_direct') {
      const expectedUrl = testConnection?.url ?? '';
      saveUrlConnectionRequest(expectedUrl);
      saveChromeExtensionRequest(testConnection?.chrome_url);
      setPendingOpen({ path: '/teste', expectedUrl });
    }
  };

  return {
    openInBrowserUrl,
    openRemote,
    handleOpenInBrowser,
    handleOpenRemote,
  };
};
